import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, SaveIcon, PackageIcon, CheckIcon, RefreshCwIcon, ArrowRightIcon, AlertCircleIcon } from 'lucide-react';
interface StorageVisualizationProps {
  locationCode: string;
  onBack: () => void;
  onPositionSelect: (positions: {
    [key: string]: boolean;
  }) => void;
  selectedPositions: {
    [key: string]: boolean;
  };
  operationType?: 'storage' | 'retrieval' | null;
  orderId?: string | null;
}
export function StorageVisualization({
  locationCode,
  onBack,
  onPositionSelect,
  selectedPositions,
  operationType = null,
  orderId = null
}: StorageVisualizationProps) {
  // Lagerplatz-Dimensionen
  const rows = 5; // Tiefe (T1 bis T5)
  const cols = 10; // Höhe (H1 bis H10)
  // Zweistufiger Auswahlprozess
  const [selectionStep, setSelectionStep] = useState<'depth' | 'height'>('depth');
  const [selectedDepth, setSelectedDepth] = useState<number | null>(null);
  const [currentPositions, setCurrentPositions] = useState<{
    [key: string]: boolean;
  }>(selectedPositions);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  // Bereits belegte Positionen (T1: H1-H5)
  const [occupiedPositions, setOccupiedPositions] = useState<{
    [key: string]: boolean;
  }>({});
  // Initialisiere belegte Positionen für T1 (H1-H5)
  useEffect(() => {
    const occupied: {
      [key: string]: boolean;
    } = {};
    // Für T1 (Index 0) sind die Höhen H1-H5 (Index 5-9) bereits belegt
    for (let i = 5; i < 10; i++) {
      occupied[`0-${i}`] = true;
    }
    // Wenn es eine Auslagerung für einen bestimmten Auftrag ist, füge die spezifischen Positionen hinzu
    if (operationType === 'retrieval' && orderId) {
      // Beispiel: Für Auftrag 1001 sind bestimmte Positionen belegt
      if (orderId === '1001' && locationCode === 'TKL-A1-01') {
        // T2-H3 für MSC Alaska Seelachs
        occupied[`1-7`] = true;
      }
      if (orderId === '1001' && locationCode === 'TKL-A2-03') {
        // T1-H5 für Hähnchen Brust
        occupied[`0-5`] = true;
      }
      if (orderId === '1001' && locationCode === 'TKL-B1-02') {
        // T3-H7 für Erbsen
        occupied[`2-3`] = true;
      }
      // Für Auftrag 1002
      if (orderId === '1002' && locationCode === 'TKL-A2-03') {
        // T1-H6 für Hähnchen Brust
        occupied[`0-4`] = true;
      }
      if (orderId === '1002' && locationCode === 'TKL-B3-05') {
        // T4-H2 für Zwiebeln
        occupied[`3-8`] = true;
      }
    }
    setOccupiedPositions(occupied);
  }, [operationType, orderId, locationCode]);
  // Funktion zum Zählen der belegten Positionen für eine bestimmte Tiefe
  const countOccupiedPositionsForDepth = (depthIndex: number) => {
    return Object.keys(occupiedPositions).filter(key => key.startsWith(`${depthIndex}-`)).length;
  };
  // Zähle die ausgewählten Positionen
  const selectedCount = Object.values(currentPositions).filter(Boolean).length;
  // Speichere die Auswahl
  const handleSaveSelection = () => {
    onPositionSelect(currentPositions);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };
  // Zurücksetzen der Auswahl
  const handleReset = () => {
    setCurrentPositions({});
    setSelectedDepth(null);
    setSelectionStep('depth');
  };
  // Auswahl der Tiefe (T1-T5)
  const handleDepthSelect = (depth: number) => {
    setSelectedDepth(depth);
    setSelectionStep('height');
  };
  // Auswahl der Höhe (H1-H10) nachdem die Tiefe ausgewählt wurde
  const handleHeightSelect = (height: number) => {
    if (selectedDepth !== null) {
      const posKey = `${selectedDepth}-${height}`;
      // Prüfen, ob die Position bereits belegt ist
      if (occupiedPositions[posKey]) {
        // Bei Auslagerung können belegte Positionen ausgewählt werden
        if (operationType === 'retrieval') {
          setCurrentPositions(prev => ({
            ...prev,
            [posKey]: !prev[posKey]
          }));
        }
        return; // Bei Einlagerung können bereits belegte Positionen nicht ausgewählt werden
      }
      setCurrentPositions(prev => ({
        ...prev,
        [posKey]: !prev[posKey]
      }));
    }
  };
  // Zurück zum ersten Schritt (Tiefenauswahl)
  const handleBackToDepthSelection = () => {
    setSelectionStep('depth');
    setSelectedDepth(null);
  };
  // Extrahiere Informationen aus dem Standortcode
  const getLocationInfo = (code: string) => {
    // Format: TKL-A1-01 (TKL = TK-Lager, A1 = Bereich, 01 = Platz)
    const parts = code.split('-');
    return {
      area: parts.length > 1 ? parts[1] : 'Unbekannt',
      position: parts.length > 2 ? parts[2] : 'Unbekannt'
    };
  };
  const locationInfo = getLocationInfo(locationCode);
  // Berechne Belegungsprozentsatz
  const occupiedCount = Object.values(occupiedPositions).filter(Boolean).length;
  const occupiedPercentage = occupiedCount / (rows * cols) * 100;
  return <div className="bg-white rounded-xl shadow-md p-6" data-id="element-2023">
      <div className="flex justify-between items-center mb-6" data-id="element-2024">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-800" data-id="element-2025">
          <ArrowLeftIcon size={16} data-id="element-2026" />
          <span data-id="element-2027">Zurück</span>
        </button>
        <h3 className="text-xl font-bold text-gray-800" data-id="element-2028">
          Lagerplatz: {locationCode}
        </h3>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2029">
        {/* Linke Spalte: Lagerplatz-Informationen */}
        <div className="lg:col-span-1" data-id="element-2030">
          <div className="bg-gray-50 rounded-lg p-4 mb-6" data-id="element-2031">
            <h4 className="font-medium text-gray-800 mb-2" data-id="element-2032">
              Lagerplatz-Details
            </h4>
            <div className="space-y-2" data-id="element-2033">
              <div className="flex justify-between" data-id="element-2034">
                <span className="text-gray-600" data-id="element-2035">Bereich:</span>
                <span className="font-medium" data-id="element-2036">{locationInfo.area}</span>
              </div>
              <div className="flex justify-between" data-id="element-2037">
                <span className="text-gray-600" data-id="element-2038">Position:</span>
                <span className="font-medium" data-id="element-2039">{locationInfo.position}</span>
              </div>
              <div className="flex justify-between" data-id="element-2040">
                <span className="text-gray-600" data-id="element-2041">Kapazität:</span>
                <span className="font-medium" data-id="element-2042">{rows * cols} Kisten</span>
              </div>
              <div className="flex justify-between" data-id="element-2043">
                <span className="text-gray-600" data-id="element-2044">Status:</span>
                <span className="font-medium text-blue-600" data-id="element-2045">
                  {occupiedCount} Plätze belegt
                </span>
              </div>
              <div className="flex justify-between" data-id="element-2046">
                <span className="text-gray-600" data-id="element-2047">Temperatur:</span>
                <span className="font-medium" data-id="element-2048">-18°C</span>
              </div>
              {orderId && <div className="flex justify-between" data-id="element-2049">
                  <span className="text-gray-600" data-id="element-2050">Auftrag:</span>
                  <span className="font-medium" data-id="element-2051">#{orderId}</span>
                </div>}
              {operationType && <div className="flex justify-between" data-id="element-2052">
                  <span className="text-gray-600" data-id="element-2053">Vorgang:</span>
                  <span className="font-medium" data-id="element-2054">
                    {operationType === 'storage' ? 'Einlagerung' : 'Auslagerung'}
                  </span>
                </div>}
            </div>
          </div>
          {/* Belegungsstatistik */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200" data-id="element-2055">
            <h4 className="font-medium text-blue-800 mb-2" data-id="element-2056">
              Aktuelle Belegung
            </h4>
            <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2" data-id="element-2057">
              <div className="bg-blue-500 h-2.5 rounded-full transition-all duration-300" style={{
              width: `${occupiedPercentage}%`
            }} data-id="element-2058"></div>
            </div>
            <div className="text-sm text-blue-700" data-id="element-2059">
              {occupiedCount} von {rows * cols} Plätzen belegt (
              {Math.round(occupiedPercentage)}%)
            </div>
          </div>
          {/* Auswahlstatistik */}
          <div className="space-y-3" data-id="element-2060">
            <button onClick={() => {
            handleSaveSelection();
            // Show success message first
            setShowSuccessMessage(true);
            // Then automatically navigate back after a short delay
            setTimeout(() => {
              setShowSuccessMessage(false);
              // Navigate back to the operation selection page
              onBack();
            }, 1500);
          }} className="w-full bg-[#8DC63F] hover:bg-[#7AB929] text-white px-4 py-2 rounded flex items-center justify-center gap-2" data-id="element-2061">
              <SaveIcon size={18} data-id="element-2062" />
              <span data-id="element-2063">Auswahl speichern</span>
            </button>
            <button onClick={handleReset} className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded flex items-center justify-center gap-2" data-id="element-2064">
              <RefreshCwIcon size={18} data-id="element-2065" />
              <span data-id="element-2066">Auswahl zurücksetzen</span>
            </button>
          </div>
          {showSuccessMessage && <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center" data-id="element-2067">
              <CheckIcon size={18} className="text-green-600 mr-2" data-id="element-2068" />
              <span className="text-green-700" data-id="element-2069">
                Auswahl erfolgreich gespeichert!
              </span>
            </div>}
        </div>
        {/* Rechte Spalte: Visualisierung des Lagerplatzes */}
        <div className="lg:col-span-2" data-id="element-2070">
          {/* Schrittanzeige */}
          <div className="flex justify-between items-center mb-4" data-id="element-2071">
            <h5 className="font-medium text-gray-700" data-id="element-2072">
              Schritt{' '}
              {selectionStep === 'depth' ? '1: Tiefe auswählen' : '2: Höhe auswählen'}
            </h5>
            {selectionStep === 'height' && <button onClick={handleBackToDepthSelection} className="text-blue-600 hover:text-blue-800 text-sm flex items-center" data-id="element-2073">
                <ArrowLeftIcon size={14} className="mr-1" data-id="element-2074" />
                <span data-id="element-2075">Zurück zur Tiefenauswahl</span>
              </button>}
          </div>
          {/* Legende */}
          <div className="flex gap-4 mb-4" data-id="element-2076">
            <div className="flex items-center" data-id="element-2077">
              <div className="w-4 h-4 bg-blue-500 rounded mr-2" data-id="element-2078"></div>
              <span className="text-sm text-gray-600" data-id="element-2079">Belegt</span>
            </div>
            <div className="flex items-center" data-id="element-2080">
              <div className="w-4 h-4 bg-white border border-gray-300 rounded mr-2" data-id="element-2081"></div>
              <span className="text-sm text-gray-600" data-id="element-2082">Frei</span>
            </div>
            <div className="flex items-center" data-id="element-2083">
              <div className="w-4 h-4 bg-blue-500 rounded mr-2" data-id="element-2084"></div>
              <span className="text-sm text-gray-600" data-id="element-2085">Ausgewählt</span>
            </div>
          </div>
          {/* Tiefenauswahl (Schritt 1) */}
          {selectionStep === 'depth' && <div className="bg-gray-50 rounded-lg p-4 mb-4" data-id="element-2086">
              <h5 className="font-medium text-gray-700 mb-3" data-id="element-2087">
                Wählen Sie die Tiefe (T1-T5)
              </h5>
              <div className="flex" data-id="element-2088">
                {/* Tiefe-Überschrift */}
                <div className="w-10 mr-2" data-id="element-2089">
                  <div className="h-10 flex items-center justify-center font-medium text-gray-600" data-id="element-2090">
                    Tiefe
                  </div>
                </div>
                {/* Tiefe-Auswahl */}
                <div className="flex-1 space-y-2" data-id="element-2091">
                  {Array.from({
                length: rows
              }).map((_, rowIndex) => {
                // Zähle die belegten Positionen für diese Tiefe
                const occupiedPositionsCount = countOccupiedPositionsForDepth(rowIndex);
                const hasOccupiedPositions = occupiedPositionsCount > 0;
                return <div key={`depth-${rowIndex}`} className={`
                          h-10 rounded-md flex items-center justify-center transition-colors
                          ${hasOccupiedPositions ? 'bg-blue-50 border border-blue-300' : 'bg-white border border-gray-200'}
                          hover:bg-blue-50 cursor-pointer
                        `} onClick={() => handleDepthSelect(rowIndex)} data-id="element-2092">
                        <span className={`font-medium ${hasOccupiedPositions ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-2093">
                          T{rowIndex + 1}
                          {hasOccupiedPositions && ` (${occupiedPositionsCount}/${cols} belegt)`}
                        </span>
                      </div>;
              })}
                </div>
              </div>
            </div>}
          {/* Höhenauswahl (Schritt 2) - Jetzt vertikal */}
          {selectionStep === 'height' && selectedDepth !== null && <div className="bg-gray-50 rounded-lg p-4 mb-4" data-id="element-2094">
              <h5 className="font-medium text-gray-700 mb-3" data-id="element-2095">
                Ausgewählte Tiefe: T{selectedDepth + 1} - Wählen Sie die Höhe
                (H1-H10)
              </h5>
              <div className="flex" data-id="element-2096">
                {/* Höhen-Überschrift - Jetzt vertikal */}
                <div className="w-10 mr-2" data-id="element-2097">
                  {Array.from({
                length: cols
              }).map((_, colIndex) => <div key={`height-label-${colIndex}`} className="h-10 flex items-center justify-center font-medium text-gray-600" data-id="element-2098">
                      H{cols - colIndex}
                    </div>)}
                </div>
                {/* Höhen-Auswahl - Jetzt vertikal */}
                <div className="flex-1 space-y-1" data-id="element-2099">
                  {Array.from({
                length: cols
              }).map((_, colIndex) => {
                const posKey = `${selectedDepth}-${colIndex}`;
                const isSelected = currentPositions[posKey] === true;
                const isOccupied = occupiedPositions[posKey] === true;
                return <div key={`cell-${selectedDepth}-${colIndex}`} className={`
                          h-10 rounded-md flex items-center justify-center transition-colors
                          ${isOccupied ? operationType === 'retrieval' ? isSelected ? 'bg-red-500 text-white cursor-pointer' : 'bg-red-500 text-white hover:bg-red-600 cursor-pointer' : 'bg-red-500 text-white cursor-not-allowed' : isSelected ? 'bg-blue-500 text-white' : 'bg-white text-gray-400 hover:bg-gray-100 cursor-pointer'}
                          ${!isOccupied && 'border border-gray-200'}
                        `} onClick={() => handleHeightSelect(colIndex)} data-id="element-2100">
                        {isOccupied ? <PackageIcon size={18} data-id="element-2101" /> : isSelected ? <PackageIcon size={18} data-id="element-2102" /> : <div className="w-5 h-5 border-2 border-dashed border-gray-300 rounded" data-id="element-2103"></div>}
                      </div>;
              })}
                </div>
              </div>
            </div>}
          {/* Ausgewählte Positionen anzeigen */}
          {selectedCount > 0 && <div className="bg-blue-50 rounded-lg p-4 mb-4" data-id="element-2104">
              <h5 className="font-medium text-blue-700 mb-2" data-id="element-2105">
                Ausgewählte Positionen
              </h5>
              <div className="flex flex-wrap gap-2" data-id="element-2106">
                {Object.entries(currentPositions).map(([posKey, isSelected]) => {
              if (isSelected) {
                const [row, col] = posKey.split('-').map(Number);
                return <div key={`selected-${posKey}`} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm" data-id="element-2107">
                          T{row + 1}-H{cols - col}
                        </div>;
              }
              return null;
            })}
              </div>
            </div>}
          {/* Belegte Positionen anzeigen */}
          {occupiedCount > 0}
          {/* Legende und Anleitung */}
        </div>
      </div>
    </div>;
}